/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  convertToImperial,
  convertToMetres,
} from '@sportninja/common/reducers/helpers'
import { getSelfUser } from '@sportninja/common/selectors/users'
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons'
import { Alert, Image, Upload } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CopyComponent from 'src/components/CopyComponent'
import { TextInput } from 'src/components/ts-components/TextInput'
import { NumberInput } from 'src/components/ts-components/NumberInput'
import { DateOfBirthInput } from 'src/components/ts-components/DateOfBirthInput'
import ModernPhoneInput from 'src/components/ts-components/ModernPhoneInput'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import { useSelector } from 'src/hooks/useRedux'
import { useEditProfileContext } from './EditProfileContext'

const fieldWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Separator = () => {
  return (
    <div
      css={css`
        border-bottom: 1px solid ${colors.SOFT_STEEL};
        width: 100%;
      `}
    />
  )
}

const EditProfileModalContent: React.FC = () => {
  const { width } = useWindowSize()
  const isMobile = useMemo(() => width <= 768, [width])
  const { state, dispatch, beforeUpload, handleChange } =
    useEditProfileContext()
  const selfUser = useSelector((s: any) => getSelfUser(s))

  // Local height state in imperial (ft/in)
  const [heightFt, setHeightFt] = useState('')
  const [heightIn, setHeightIn] = useState('')
  const [heightInitialized, setHeightInitialized] = useState(false)

  // Init imperial from metres — runs when state.height first gets a value
  useEffect(() => {
    if (heightInitialized) {
      return
    }
    if (state.height !== '' && state.height !== undefined) {
      const metres = parseFloat(state.height)
      if (!isNaN(metres) && metres > 0) {
        const [ft, inches] = convertToImperial(metres)
        setHeightFt(String(ft))
        setHeightIn(String(inches))
      }
      setHeightInitialized(true)
    }
  }, [state.height, heightInitialized])

  const handleHeightFtChange = useCallback(
    (value: string) => {
      setHeightFt(value)
      const metres = convertToMetres({ ft: value, inches: heightIn || '0' })
      dispatch({
        type: 'SET_FIELD',
        field: 'height',
        value: metres !== false ? String(metres) : '',
      })
    },
    [heightIn, dispatch]
  )

  const handleHeightInChange = useCallback(
    (value: string) => {
      setHeightIn(value)
      const metres = convertToMetres({ ft: heightFt || '0', inches: value })
      dispatch({
        type: 'SET_FIELD',
        field: 'height',
        value: metres !== false ? String(metres) : '',
      })
    },
    [heightFt, dispatch]
  )

  const handleFieldChange = useCallback(
    (field: string) => (value: string) => {
      dispatch({ type: 'SET_FIELD', field, value })
    },
    [dispatch]
  )

  const handlePhoneChange = useCallback(
    (value: string | undefined) => {
      dispatch({ type: 'SET_FIELD', field: 'phone_number', value: value || '' })
    },
    [dispatch]
  )

  const handlePhoneCountryChange = useCallback(
    (country: string | undefined) => {
      dispatch({
        type: 'SET_FIELD',
        field: 'phone_number_country',
        value: country || '',
      })
    },
    [dispatch]
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 16px;
        height: 100%;
        overflow-y: auto;
        width: 100%;
      `}
    >
      {/* Header with ID */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
        `}
      >
        <Text variant='captionLarge' weight='semiBold'>
          Profile Info
        </Text>
        {selfUser?.id ? (
          <div
            css={css`
              margin-left: auto;
            `}
          >
            <CopyComponent id={selfUser.id} />
          </div>
        ) : null}
      </div>

      {/* Profile Image Upload */}
      {state.image_url ? (
        <div
          css={css`
            display: flex;
            padding: 8px;
            align-items: center;
            gap: 8px;
            align-self: stretch;
            border-radius: 8px;
            background-color: ${colors.SECONDARY_200};
            border: 1px solid ${colors.SECONDARY_100};
            width: 100%;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 8px;
              flex: 1 0 0;
            `}
          >
            <Image
              src={state.image_url}
              alt='profile photo'
              width={56}
              height={56}
              css={css`
                border-radius: 50%;
                object-fit: cover;
              `}
              preview={false}
              onError={(e) => {
                e.currentTarget.src =
                  '/images/generic_placeholders/placeholder_player.png'
              }}
            />
            <DeleteOutlined
              css={css`
                color: ${colors.ERROR_200};
                font-size: 24px;
                cursor: pointer;
              `}
              onClick={() => {
                dispatch({
                  type: 'SET_IMAGE',
                  payload: { url: null, data: null, changed: true },
                })
              }}
            />
          </div>
        </div>
      ) : (
        <Upload
          beforeUpload={beforeUpload}
          onChange={handleChange}
          showUploadList={false}
          action={undefined}
          css={css`
            display: block;
            width: 100%;
            .ant-upload {
              display: block !important;
              width: 100% !important;
            }
          `}
        >
          <div
            css={css`
              display: flex;
              padding: 8px;
              align-items: center;
              gap: 8px;
              align-self: stretch;
              border-radius: 8px;
              border: 1px dashed ${colors.SECONDARY_100};
              cursor: pointer;
              width: 100%;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                flex: 1 0 0;
              `}
            >
              <div
                css={css`
                  display: flex;
                  width: 48px;
                  height: 48px;
                  flex-direction: column;
                  justify-content: center;
                  align-items: center;
                  border-radius: 50%;
                  background: ${colors.PRIMARY};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    width: 24px;
                    height: 24px;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    border-radius: 50%;
                    background: ${colors.WHITE};
                  `}
                >
                  <PlusCircleOutlined
                    css={css`
                      font-size: 24px;
                      color: ${colors.SECONDARY_300};
                      background-color: ${colors.PRIMARY};
                    `}
                  />
                </div>
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  justify-content: center;
                  align-items: flex-start;
                `}
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                >
                  Profile Photo
                </Text>
                <Text
                  variant='paragraphSmall'
                  weight='medium'
                  color={colors.PRIMARY}
                >
                  Click to Upload
                </Text>
              </div>
            </div>
          </div>
        </Upload>
      )}

      {/* First Name + Last Name */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='name_first'
            label='First Name'
            value={state.name_first}
            setValue={handleFieldChange('name_first')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='First Name'
            required
          />
        </div>
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <TextInput
            name='name_last'
            label='Last Name'
            value={state.name_last}
            setValue={handleFieldChange('name_last')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            placeholder='Last Name'
            required
          />
        </div>
      </div>

      <Separator />

      {/* Height (ft + in) + Weight - 3 columns */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          ${isMobile &&
          css`
            flex-direction: column;
          `}
        `}
      >
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <NumberInput
            name='height_ft'
            label='Feet'
            value={heightFt}
            setValue={handleHeightFtChange}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            min={0}
            max={10}
            placeholder='ft'
          />
        </div>
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <NumberInput
            name='height_in'
            label='Inches'
            value={heightIn}
            setValue={handleHeightInChange}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            min={0}
            max={11}
            placeholder='in'
          />
        </div>
        <div
          css={css`
            flex: 1;
            ${fieldWrapperStyle}
          `}
        >
          <NumberInput
            name='weight'
            label='Weight (lbs)'
            value={state.weight}
            setValue={handleFieldChange('weight')}
            fieldErrors={state.fieldErrors}
            customError={state.customError}
            min={0}
            placeholder='Weight'
          />
        </div>
      </div>

      <Separator />

      {/* Date of Birth */}
      <div css={fieldWrapperStyle}>
        <DateOfBirthInput
          name='birth_date'
          label='Date of Birth'
          value={state.birth_date}
          setValue={handleFieldChange('birth_date')}
          fieldErrors={state.fieldErrors}
          customError={state.customError}
        />
      </div>

      {/* Phone Number */}
      <div css={fieldWrapperStyle}>
        <ModernPhoneInput
          value={state.phone_number}
          onChange={handlePhoneChange}
          onCountryChange={handlePhoneCountryChange}
          label='Phone Number'
          error={
            state.fieldErrors?.includes('phone_number')
              ? (state.customError?.phone_number as string) ||
                'Invalid phone number'
              : ''
          }
          validateOnBlur={false}
        />
      </div>

      {/* Error Display */}
      {state.error && (
        <Alert
          message={state.error}
          type='error'
          showIcon
          css={css`
            background: ${colors.ERROR_50};
            border: 1px solid ${colors.ERROR_300};
            border-radius: 8px;
            margin-top: 8px;
            .ant-alert-message {
              color: ${colors.ERROR_300};
            }
            .ant-alert-icon {
              color: ${colors.ERROR_300};
            }
          `}
        />
      )}
    </div>
  )
}

export default EditProfileModalContent
